import React from 'react';
import { HiArrowLeft } from 'react-icons/hi';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui';
import { LoadingSpinner } from '../ui/LoadingSpinner';

interface DetailPageLayoutProps {
  title: string;
  subtitle?: string | React.ReactNode;
  children: React.ReactNode;
  actions?: React.ReactNode;
  secondaryActions?: React.ReactNode;
  badges?: React.ReactNode;
  isLoading?: boolean;
  isError?: boolean;
  errorMessage?: string;
  onRetry?: () => void;
  showBackButton?: boolean;
  onBack?: () => void;
  maxWidth?: string;
  disableScroll?: boolean;
}

export const DetailPageLayout: React.FC<DetailPageLayoutProps> = ({
  title,
  subtitle,
  children,
  actions,
  secondaryActions,
  badges: _badges,
  isLoading = false,
  isError = false,
  errorMessage = '데이터를 불러오는 중 오류가 발생했습니다.',
  onRetry,
  showBackButton = true,
  onBack,
  maxWidth = 'max-w-5xl',
  disableScroll = false,
}) => {
  const navigate = useNavigate();

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate(-1);
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex h-full w-full items-center justify-center p-8">
        <div className="max-w-md text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
            <svg className="h-8 w-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h3 className="mb-2 text-lg font-bold text-slate-900">오류가 발생했습니다</h3>
          <p className="mb-6 text-slate-500">{errorMessage}</p>
          <div className="flex justify-center gap-3">
            <Button onClick={handleBack} variant="outline">
              뒤로가기
            </Button>
            {onRetry && (
              <Button onClick={onRetry} variant="primary">
                다시 시도
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex-1 ${disableScroll ? 'overflow-visible' : 'overflow-auto'} bg-slate-50/50 p-6 md:p-8`}>
      <div className={`${maxWidth} mx-auto`}>
        {/* Header */}
        <div className="mb-8 flex flex-col justify-between gap-4 md:flex-row md:items-center">
          <div className="flex items-start gap-4">
            {showBackButton && (
              <button
                onClick={handleBack}
                className="mt-1 rounded-lg p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
                aria-label="Go back">
                <HiArrowLeft className="h-6 w-6" />
              </button>
            )}
            <div>
              <h2 className="text-2xl font-bold text-slate-900 md:text-3xl">{title}</h2>
              {subtitle && <div className="mt-1 text-sm text-slate-500 md:text-base">{subtitle}</div>}
            </div>
          </div>

          {(actions || secondaryActions) && (
            <div className="flex flex-wrap items-center gap-3 md:flex-nowrap">
              {secondaryActions}
              {actions}
            </div>
          )}
        </div>

        {/* Content */}
        <div className="animate-fade-in space-y-6">{children}</div>
      </div>
    </div>
  );
};
